import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Filter, Check, ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import type { Band } from '../../types';

const INTEREST_LEVELS = [
  { value: 'no_information', label: 'No Info' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'not_interested_for_now', label: 'Not Now' },
  { value: 'mildly_interested', label: 'Mildly Interested' },
  { value: 'very_interested', label: 'Very Interested' },
  { value: 'concert_proposal', label: 'Concert Proposal' },
];

interface InterestColumnFilterProps {
  bands: Band[];
  selectedLevels: string[];
  selectedBand: string | undefined; // undefined = "any", "overall" = all, or band_id
  onChangeLevel: (levels: string[]) => void;
  onChangeBand: (band: string | undefined) => void;
  onChangeBoth?: (band: string | undefined, levels: string[]) => void;
}

type CategoryKey = 'any' | 'overall' | string; // string = band_id

export function InterestColumnFilter({
  bands,
  selectedLevels,
  selectedBand,
  onChangeLevel,
  onChangeBand,
  onChangeBoth,
}: InterestColumnFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<CategoryKey | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasActiveFilter = selectedLevels.length > 0;

  // Determine which category is active based on selectedBand
  const activeCategory: CategoryKey = selectedBand === undefined ? 'any' : selectedBand === 'overall' ? 'overall' : selectedBand;

  // Auto-expand the active category when opening
  useEffect(() => {
    if (isOpen) {
      setExpandedCategory(hasActiveFilter ? activeCategory : null);
    }
  }, [isOpen]);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current || !dropdownRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 280;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = Math.max(8, rect.right - dropdownWidth);
    }
    // Always open downward — the filter button is in the sticky table header near the
    // top of the viewport, so opening upward risks being clipped by the navbar or viewport edge.
    const top = rect.bottom + 4;
    const maxH = viewportHeight - top - 8;
    setDropdownPosition({ top, left });
    dropdownRef.current.style.maxHeight = `${Math.max(maxH, 120)}px`;
    setIsPositioned(true);
  }, []);

  // Position dropdown when opened — render offscreen first, then measure & place
  useEffect(() => {
    if (isOpen) {
      setIsPositioned(false);
      requestAnimationFrame(updatePosition);
    }
  }, [isOpen, updatePosition]);

  // Reposition after category expand/collapse changes dropdown height
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(updatePosition);
    }
  }, [expandedCategory, updatePosition]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggleLevel = (category: CategoryKey, level: string) => {
    // If switching categories, start fresh with just this level
    if (category !== activeCategory) {
      const bandValue = category === 'any' ? undefined : category === 'overall' ? 'overall' : category;
      if (onChangeBoth) {
        onChangeBoth(bandValue, [level]);
      } else {
        onChangeBand(bandValue);
        onChangeLevel([level]);
      }
      return;
    }
    // Same category: toggle the level
    if (selectedLevels.includes(level)) {
      onChangeLevel(selectedLevels.filter(l => l !== level));
    } else {
      onChangeLevel([...selectedLevels, level]);
    }
  };

  const handleSelectAll = (category: CategoryKey) => {
    const bandValue = category === 'any' ? undefined : category === 'overall' ? 'overall' : category;
    if (category !== activeCategory) {
      if (onChangeBoth) {
        onChangeBoth(bandValue, INTEREST_LEVELS.map(l => l.value));
        return;
      }
      onChangeBand(bandValue);
    }
    onChangeLevel(INTEREST_LEVELS.map(l => l.value));
  };

  const handleClearCategory = (category: CategoryKey) => {
    if (category === activeCategory) {
      onChangeLevel([]);
    }
  };

  const toggleCategory = (category: CategoryKey) => {
    setExpandedCategory(prev => prev === category ? null : category);
  };

  // Get selected count for a category
  const getSelectedCount = (category: CategoryKey) => {
    if (category !== activeCategory) return 0;
    return selectedLevels.length;
  };

  const categories: { key: CategoryKey; label: string }[] = [
    { key: 'any', label: 'Any' },
    { key: 'overall', label: 'All' },
    ...bands.map(b => ({ key: b._id, label: b.name })),
  ];

  return (
    <div className="relative inline-flex">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={clsx(
          'p-0.5 rounded-sm transition-colors',
          hasActiveFilter
            ? 'text-primary-400 hover:text-primary-300'
            : 'text-dark-text-muted hover:text-dark-text-secondary opacity-0 group-hover:opacity-100'
        )}
        title="Filter by interest"
      >
        <Filter className="w-3.5 h-3.5" />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={clsx(
            'fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl w-[280px] flex flex-col overflow-hidden',
            isPositioned ? 'animate-scaleIn' : 'opacity-0 pointer-events-none'
          )}
          style={isPositioned
            ? { top: dropdownPosition.top, left: dropdownPosition.left }
            : { top: -9999, left: -9999 }
          }
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-dark-border">
            <span className="text-xs font-medium text-dark-text-secondary">Filter by Interest</span>
          </div>

          <div className="flex-1 overflow-y-auto py-1">
            {categories.map(({ key, label }) => {
              const count = getSelectedCount(key);
              const isExpanded = expandedCategory === key;

              return (
                <div key={key}>
                  {/* Category header */}
                  <button
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors hover:bg-dark-surface',
                      count > 0 && 'text-primary-300'
                    )}
                    onClick={() => toggleCategory(key)}
                  >
                    {isExpanded
                      ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                      : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                    }
                    <span className="flex-1">{label}</span>
                    {count > 0 && (
                      <span className="text-xs text-primary-400 bg-primary-600/20 px-1.5 py-0.5 rounded-sm">
                        {count}
                      </span>
                    )}
                  </button>

                  {/* Expanded: show interest levels */}
                  {isExpanded && (
                    <div className="ml-4 border-l border-dark-border">
                      {/* Quick actions */}
                      <div className="flex items-center gap-2 px-3 py-1 text-xs">
                        <button
                          onClick={() => handleSelectAll(key)}
                          className="text-primary-400 hover:text-primary-300 transition-colors"
                        >
                          Select all
                        </button>
                        <span className="text-dark-text-muted">|</span>
                        <button
                          onClick={() => handleClearCategory(key)}
                          className="text-dark-text-secondary hover:text-dark-text-primary transition-colors"
                        >
                          Clear
                        </button>
                      </div>

                      {/* Interest level checkboxes */}
                      {INTEREST_LEVELS.map(({ value, label: levelLabel }) => {
                        const isSelected = key === activeCategory && selectedLevels.includes(value);
                        return (
                          <button
                            key={value}
                            onClick={() => handleToggleLevel(key, value)}
                            className={clsx(
                              'w-full flex items-center gap-2 px-3 py-1 text-sm text-left transition-colors',
                              isSelected
                                ? 'bg-primary-600/20 text-primary-300'
                                : 'text-dark-text-secondary hover:bg-dark-surface'
                            )}
                          >
                            <div
                              className={clsx(
                                'w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0',
                                isSelected
                                  ? 'bg-primary-500 border-primary-500'
                                  : 'border-dark-border'
                              )}
                            >
                              {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                            <span className="text-xs">{levelLabel}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-end gap-2 px-2 py-2 border-t border-dark-border">
            <button
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 text-xs text-dark-text-secondary hover:text-dark-text-primary transition-colors"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
